import React, { FC, useState } from 'react';
import styled from 'styled-components';
import { Question, QuestionOption, QuizAnswer } from 'types/quiz';
import MainImage from 'assets/icons/Group1.svg';
import Text from 'components/Text';
import ContinueButton from './QuizContinueButton';
import theme from 'utils/theme';
import { DynamicImage } from 'components';
import { mobile, tablet } from 'styles/breakpoints';

const Container = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  align-items: center;
  margin-top: 1.5rem;
`;

const Title = styled(Text)`
  font-weight: 700;
  font-size: 1.25rem;
  color: ${({ theme }) => theme.colors.dark100};
  text-align: center;
  margin-top: 1.5rem;
  max-width: 18.75rem;
  line-height: 130%;
`;

const BlockText = styled(Text)<{ selected: boolean }>`
  font-size: 14px;
  color: ${({ theme, selected }) =>
    selected ? theme.colors.cultured : theme.colors.dark100};
`;

const ScaleTextWrapper = styled.div`
  display: flex;
  justify-content: space-between;
  padding: 0 0.425rem;
  margin-bottom: 0.4375rem;
`;

const ScaleText = styled(Text)`
  max-width: 3.4375rem;
  font-weight: 400;
  font-size: 0.75rem;
  text-align: center;
`;

const ScaleWrapper = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  max-width: 21.4375rem;
  border-radius: 0.375rem;
  margin-top: 1.5rem;
`;

const ScaleBody = styled.div`
  display: flex;
  height: 2.75rem;
`;

const ScaleItem = styled.div<{ selected: boolean; background: string }>`
  cursor: pointer;
  width: 20%;
  height: 100%;
  font-weight: 400;
  font-size: 0.875rem;

  display: flex;
  justify-content: center;
  align-items: center;

  background: ${({ selected, background }) =>
    selected ? background : theme.colors.cultured};

  border: ${({ theme }) => `2px solid ${theme.colors.light60}`};
  border-right-width: 0;

  &:nth-child(1) {
    border-radius: 0.375rem 0rem 0rem 0.375rem;
  }

  &:nth-last-child(1) {
    border-radius: 0rem 0.375rem 0.375rem 0rem;
    border-right: ${({ theme }) => `2px solid ${theme.colors.light60}`};
  }
`;

const StyledDynamicImage = styled(DynamicImage)<{
  imageHeightDesktop: string;
  imageHeightTablet: string;
  imageHeightMobile: string;
}>`
  height: ${({ imageHeightDesktop }) => imageHeightDesktop};

  @media ${tablet} {
    height: ${({ imageHeightTablet }) => imageHeightTablet};
  }

  @media ${mobile} {
    height: ${({ imageHeightMobile }) => imageHeightMobile};
  }
`;

interface ScaleProps {
  question: Question;
  onContinue: (value: string) => void;
}
const Scale: FC<ScaleProps> = ({ question, onContinue }) => {
  const [scaleData, setScaleData] = useState([
    { value: 1, selected: false, background: theme.colors.scale1 },
    { value: 2, selected: false, background: theme.colors.scale2 },
    { value: 3, selected: false, background: theme.colors.scale3 },
    { value: 4, selected: false, background: theme.colors.scale4 },
    { value: 5, selected: false, background: theme.colors.scale5 },
  ]);

  const [currentScale, setCurrentScale] = useState(0);

  const handleChangeScale = (value: number) => {
    const newScaleData = scaleData.map(scale => {
      scale.selected = value >= scale.value ? true : false;
      return scale;
    });
    setCurrentScale(value);
    setScaleData(newScaleData);
  };

  const isDisabled = currentScale <= 0;

  return (
    <Container>
      {question.image && question.alt ? (
        <StyledDynamicImage
          src={question.image}
          alt={question.alt}
          imageHeightDesktop={question.imageHeightDesktop}
          imageHeightTablet={question.imageHeightTablet}
          imageHeightMobile={question.imageHeightMobile}
        />
      ) : null}
      <Title type="h1" color="dark100">
        {question.title}
      </Title>
      <ScaleWrapper>
        <ScaleTextWrapper>
          <ScaleText>{question.scaleHighest}</ScaleText>
          <ScaleText>{question.scaleLowest}</ScaleText>
        </ScaleTextWrapper>
        <ScaleBody>
          {scaleData.map(scale => (
            <ScaleItem
              key={scale.value}
              selected={scale.selected}
              background={
                scaleData[currentScale === 0 ? 0 : currentScale - 1].background
              }
              onClick={() => handleChangeScale(scale.value)}
            >
              <BlockText selected={scale.selected}>{scale.value}</BlockText>
            </ScaleItem>
          ))}
        </ScaleBody>
      </ScaleWrapper>
      <ContinueButton
        title={question?.nextBtnTitle}
        onClick={() => onContinue(currentScale.toString())}
        disabled={isDisabled}
      />
    </Container>
  );
};

export default Scale;
